import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "../config";
import { useAuth } from "../context/AuthContext";

async function fetchWithAuth(url, options = {}, token) {
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Request failed");
  }

  return data;
}

export function useEC2Instances() {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["ec2Instances"],
    queryFn: async () => {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/v1/ec2-instances`, {}, token);
      if (data.success) {
        return {
          instances: data.instances,
          isAdmin: data.isAdmin,
          maxInstances: data.maxInstances,
          users: data.users || [],
        };
      }
      throw new Error(data.message || "Failed to fetch instances");
    },
    refetchInterval: 30000,
  });
}

export function useProfiles() {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["profiles"],
    queryFn: async () => {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/v1/profiles`, {}, token);
      if (data.success) {
        return data.profiles;
      }
      throw new Error(data.message || "Failed to fetch profiles");
    },
  });
}

export function useDeployInstance() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ instanceName, profile, deployType }) => {
      return fetchWithAuth(
        `${API_BASE_URL}/api/v1/deploy-demo`,
        {
          method: "POST",
          body: JSON.stringify({
            instanceName,
            profile: profile || undefined,
            deployType: deployType === "ecs" ? "ecs" : "ec2",
          }),
        },
        token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ec2Instances"] });
    },
  });
}

export function useDestroyInstance() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ stackName, publicIp }) => {
      const url = publicIp
        ? `${API_BASE_URL}/api/v1/destroy-instance/${stackName}?publicIp=${encodeURIComponent(publicIp)}`
        : `${API_BASE_URL}/api/v1/destroy-instance/${stackName}`;
      return fetchWithAuth(url, { method: "DELETE" }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ec2Instances"] });
    },
  });
}

export function useInstanceLogs(publicIp) {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["instanceLogs", publicIp],
    queryFn: () => fetchWithAuth(`${API_BASE_URL}/api/v1/instance-logs/${publicIp}`, {}, token),
    enabled: !!publicIp && publicIp !== "N/A",
    refetchOnWindowFocus: false,
  });
}

export function useDockerStatus(publicIp) {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["dockerStatus", publicIp],
    queryFn: async () => {
      const data = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/docker-status/${publicIp}`,
        {},
        token
      );
      return data.containers || [];
    },
    enabled: !!publicIp && publicIp !== "N/A",
  });
}

export function useDockerLogs(publicIp, containerName) {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["dockerLogs", publicIp, containerName],
    queryFn: () =>
      fetchWithAuth(
        `${API_BASE_URL}/api/v1/docker-logs/${publicIp}/${containerName}?lines=200`,
        {},
        token
      ),
    enabled: !!publicIp && publicIp !== "N/A" && !!containerName,
    refetchOnWindowFocus: false,
  });
}

export function useRestartContainer() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ publicIp, containerName }) => {
      return fetchWithAuth(
        `${API_BASE_URL}/api/v1/docker-restart/${publicIp}/${containerName}`,
        { method: "POST" },
        token
      );
    },
    onSuccess: (_, { publicIp }) => {
      queryClient.invalidateQueries({ queryKey: ["dockerStatus", publicIp] });
    },
  });
}

export function useHealthCheck() {
  return useQuery({
    queryKey: ["healthCheck"],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/v1/health`);
      return response.json();
    },
    refetchOnWindowFocus: false,
  });
}


export function useSetIndefinite() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ instanceId, indefinite }) => {
      return fetchWithAuth(
        `${API_BASE_URL}/api/v1/instance/${instanceId}/indefinite`,
        {
          method: "PATCH",
          body: JSON.stringify({ indefinite }),
        },
        token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ec2Instances"] });
    },
  });
}

export function useUploadConfig() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ publicIp, containerName, file }) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${API_BASE_URL}/api/v1/instance/${publicIp}/container/${containerName}/upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error(`Upload failed (HTTP ${response.status})`);
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Upload failed");
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ec2Instances"] });
    },
  });
}

export function useDeployProgress(publicIp, enabled = false) {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["deployProgress", publicIp],
    queryFn: async () => {
      const data = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/instance/${publicIp}/deploy-progress`,
        {},
        token
      );
      return data;
    },
    enabled: enabled && !!publicIp && publicIp !== "N/A",
    refetchInterval: enabled ? 5000 : false,
    refetchOnWindowFocus: false,
  });
}

export function useChangeOwner() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ instanceId, userId }) => {
      return fetchWithAuth(
        `${API_BASE_URL}/api/v1/instance/${instanceId}/owner`,
        {
          method: "PATCH",
          body: JSON.stringify({ userId }),
        },
        token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ec2Instances"] });
    },
  });
}
